import { SectionsRow } from "@outschool/db-queries";

import { pathToUrl } from "./config";
import {
  ActivitySlugProps,
  addParamsToUrl,
  objectToQueryString,
} from "./utils";
import { WebsiteRoutes, sectionOrActivityPath } from "./websiteRoutes";

export class SharingUrls {
  constructor(
    private websiteRoutes: WebsiteRoutes,
    private facebookAppId: string
  ) {}

  private sectionOrActivityUrl(
    activity: ActivitySlugProps,
    section?: Pick<SectionsRow, "uid">,
    referrer?: { slug_id: string }
  ): string {
    return addParamsToUrl(
      this.websiteRoutes.sectionOrActivityUrl(
        activity,
        section?.uid,
        undefined,
        section ? true : undefined
      ),
      {
        usid: referrer ? referrer.slug_id : undefined,
        signup: true,
      }
    );
  }

  /** Create url for Facebook's share dialog from an activity. Optionally, it can
   *  take a section, a referring user, a quote, and a tracking campaign prefix.
   *
   *  This url has the ability to show the option to share to Facebook groups. FYI
   *
   *  See https://developers.facebook.com/docs/sharing/reference/share-dialog
   */
  public facebookActivityShareUrl(
    activity: ActivitySlugProps,
    section?: Pick<SectionsRow, "uid">,
    referrer?: { slug_id: string },
    quote?: string,
    campaignPrefix?: string
  ): string {
    const shareUrl = "https://www.facebook.com/dialog/share?";
    const params = {
      app_id: this.facebookAppId,
      display: "page",
      href: addParamsToUrl(
        this.sectionOrActivityUrl(activity, section, referrer),
        { utm_campaign: `${campaignPrefix || "share_activity"}_facebook` }
      ),
      redirect_uri: this.websiteRoutes.baseUrl,
      quote: quote,
    };

    return (
      // hashtag should be prefixed with # and not urlescaped
      shareUrl + objectToQueryString(params) + "&hashtag=#outschool"
    );
  }
}

// TODO: choose between the class and the functions
// move all SharingUrls call sites to exported urls, or vice versa

/** Create url for Facebook's share dialog from an activity. Optionally, it can
 *  take a section, a referring user, a quote, and a tracking campaign prefix.
 *
 *  This url has the ability to show the option to share to Facebook groups. FYI
 *
 *  See https://developers.facebook.com/docs/sharing/reference/share-dialog
 */
export function facebookActivityShareUrl({
  activity,
  section,
  referrer,
  quote,
  campaignPrefix,
  appId,
}: {
  activity: { uid: string; slug_id: string; title: string; isClub: boolean };
  section?: string;
  referrer?: { slug_id: string };
  quote?: string;
  campaignPrefix?: string;
  appId?: string;
}): string {
  const shareUrl = "https://www.facebook.com/dialog/share?";
  const params = {
    app_id: appId,
    display: "page",
    href: addParamsToUrl(pathToUrl(sectionOrActivityPath(activity, section)), {
      usid: referrer ? referrer.slug_id : undefined,
      utm_campaign: `${campaignPrefix || "share_activity"}_facebook`,
    }),
    redirect_uri: pathToUrl("/"),
    quote: quote,
  };

  return (
    // hashtag should be prefixed with # and not urlescaped
    shareUrl + objectToQueryString(params) + "&hashtag=#outschool"
  );
}

// See https://developers.facebook.com/docs/sharing/reference/share-dialog
export function facebookGeneralShareUrl(url: string, appId?: string): string {
  const shareUrl = "https://www.facebook.com/dialog/share?";
  const params = {
    app_id: appId,
    display: "page",
    href: url,
    redirect_uri: pathToUrl("/"),
  };

  return (
    // hashtag should be prefixed with # and not urlescaped
    shareUrl + objectToQueryString(params) + "&hashtag=#outschool"
  );
}

// See https://developers.facebook.com/docs/sharing/reference/send-dialog
export function facebookGeneralSendUrl(url: string, appId?: string): string {
  const shareUrl = "https://www.facebook.com/dialog/send?";
  const params = {
    app_id: appId,
    link: url,
    redirect_uri: pathToUrl("/"),
  };

  return shareUrl + objectToQueryString(params);
}
